<template>
  <div id="app">
    <keep-alive>
    <router-view/>
    </keep-alive>
  </div>
</template>

<style lang="stylus">

    @import 'style/colors.styl';
    @import 'style/mixins.styl';
    @import 'style/spacing.styl';
    @import 'style/responsive.styl';

    // Prevent horizontal scroll on mobile
    html, body
        overflow-x hidden
        width 100%

    #app, html
        color color-theme(true)
        font-family 'Manrope-Bold', 'Roboto-Regular', sans-serif

    // Better touch targets for mobile
    @media (max-width: 768px)
        button, .btn, a, input, select, textarea
            min-height 44px

        // Improve form inputs on mobile
        input, select, textarea
            font-size 16px !important // Prevents zoom on iOS

    // Smooth scrolling
    html
        scroll-behavior smooth

    // Better box-sizing
    *, *::before, *::after
        box-sizing border-box

    // Global focus styles for accessibility
    *:focus
        outline-color primary
        outline-width 2px
        outline-offset 2px

    // Button styles
    .btn-outline-primary
        color accent
        border-color accent
        transition all transition-base

        &:hover
          background-color accent
          border-color accent
          color white
          transform translateY(-1px)
          box-shadow shadow-sm

        &:not(:disabled):not(.disabled):active
            background-color accent
            border-color accent
            color white

        &:focus
            box-shadow 0 0 0 0.2rem alpha(accent, 0.5)

    // Form controls
    .form-control, .form-control:focus, .custom-select, .custom-select:focus
        background-color white
        color color-theme(true)
        border-color accent
        border-width 2px
        border-radius radius-md
        transition all transition-base
        height 48px
        font-size 1rem

    .form-control:focus, .custom-select:focus
        box-shadow 0 0 0 4px alpha(primary, .15)
        outline none
        border-color primary

    // Links
    a
      transition all transition-fast

      &:hover
        text-decoration none

    // Tables
    table
        width 100%

    // Icon buttons with modern gradient
    .btn-icon
        border-radius radius-md
        background linear-gradient(135deg, accent 0%, darken(accent, 10%) 100%)
        color white
        padding space-sm
        cursor pointer
        no-select()
        transition all transition-base
        margin 0 space-sm
        display inline-flex
        align-items center
        justify-content center
        min-width 40px
        min-height 40px
        box-shadow shadow-sm

        &:hover
            background linear-gradient(135deg, primary 0%, #FF7A29 100%)
            transform translateY(-2px)
            box-shadow shadow-orange-lg

        &:active
            transform translateY(0)

        &:focus
            outline 2px solid primary
            outline-offset 2px

    .btn-icons
        display flex
        justify-content center
        gap space-sm
        align-items center

    // Scrollbar styling
    ::-webkit-scrollbar
        width 8px
        height 8px

    ::-webkit-scrollbar, ::-webkit-scrollbar-corner
        background content-theme()

    ::-webkit-scrollbar-track
        border-radius radius-md
        background color-theme(true)
        background-clip content-box
        border 3px solid transparent

    ::-webkit-scrollbar-thumb
        background linear-gradient(180deg, accent 0%, primary 100%)
        border-radius radius-md
        transition all transition-base

        &:hover
            background linear-gradient(180deg, primary 0%, accent-hover 100%)

    // Loading states
    .loading-spinner
        animation spin 1s linear infinite

    @keyframes spin
        from
            transform rotate(0deg)
        to
            transform rotate(360deg)

    // Ripple effect for buttons
    .btn-ripple
        position relative
        overflow hidden

        &::after
            content ''
            position absolute
            top 50%
            left 50%
            width 0
            height 0
            border-radius 50%
            background rgba(255, 255, 255, 0.5)
            transform translate(-50%, -50%)
            transition width 0.6s, height 0.6s

        &:active::after
            width 200px
            height 200px

    // Toast notifications
    .b-toast
        .toast
            border-radius radius-lg
            box-shadow shadow-lg
            border none

        .toast-header
            border-radius radius-lg radius-lg 0 0
            font-weight 600

        .toast-body
            font-size 0.95rem

    // Modal backdrop blur
    .modal-backdrop
        backdrop-filter blur(8px)
        background-color rgba(0, 0, 0, 0.5)

    // Card enhancements
    .card
        border-radius radius-xl
        border none
        box-shadow shadow-md
        transition all transition-base

        &:hover
            box-shadow shadow-lg

    // Badge styles
    .badge
        border-radius radius-pill
        padding space-xs space-sm
        font-weight 600
        font-size 0.85rem

    .badge-primary
        background linear-gradient(135deg, primary 0%, #FF7A29 100%)

    .connection-toast-content
        display flex
        align-items center
        gap space-sm
        font-weight 600

        .b-icon
            font-size 1.25rem

    // Global responsive styles
    // Tables
    .table-responsive
        display block
        width 100%
        overflow-x auto
        -webkit-overflow-scrolling touch

    // Responsive pagination
    @media (max-width: 576px)
        .pagination
            flex-wrap wrap
            justify-content center
            gap space-xs

            .page-link
                padding space-xs space-sm
                font-size 0.85rem

            .page-item
                margin 2px

    // Responsive Bootstrap row/col adjustments
    @media (max-width: 768px)
        .row
            margin-left 0
            margin-right 0

        [class*="col-"]
            padding-left space-sm
            padding-right space-sm

        .container, .container-fluid
            padding-left space-md
            padding-right space-md

    @media (max-width: 576px)
        [class*="col-"]
            padding-left space-xs
            padding-right space-xs

        .container, .container-fluid
            padding-left space-sm
            padding-right space-sm

    // Form group responsive
    @media (max-width: 768px)
        .form-group
            margin-bottom space-md

        .form-row
            flex-direction column

            > [class*="col-"]
                margin-bottom space-md
                max-width 100%
                flex 0 0 100%

    // Button responsive
    @media (max-width: 576px)
        .btn
            padding space-sm space-md
            font-size 0.9rem

        .btn-lg
            padding space-sm space-lg
            font-size 1rem

        .btn-sm
            padding space-xs space-sm
            font-size 0.8rem

    // Modal responsive
    @media (max-width: 576px)
        .modal-body
            padding space-md

        .modal-header, .modal-footer
            padding space-sm space-md

        .modal-title
            font-size 1.1rem

    // Form control responsive
    @media (max-width: 768px)
        .form-control, .custom-select
            height 44px
            font-size 16px
            padding space-sm space-md

    // Datepicker responsive
    @media (max-width: 768px)
        .b-form-datepicker
            width 100%

        .b-calendar
            max-width 100%

        .b-calendar-grid
            font-size 0.85rem

    // Table responsive enhancements
    .table-responsive, [class*="table-responsive"]
        display block
        width 100%
        overflow-x auto
        -webkit-overflow-scrolling touch

        &::-webkit-scrollbar
            height 6px

        &::-webkit-scrollbar-thumb
            background linear-gradient(90deg, accent 0%, primary 100%)
            border-radius radius-md

    @media (max-width: 768px)
        .table-responsive, [class*="table-responsive"]
            margin 0 -12px
            padding 0 12px

    // B-Table specific responsive
    @media (max-width: 768px)
        .b-table-sticky-header
            max-height 60vh !important

        .table thead th
            position sticky
            top 0
            z-index 1

    // Card responsive
    @media (max-width: 768px)
        .card
            border-radius radius-md

        .card-body
            padding space-sm

    @media (max-width: 576px)
        .card-body
            padding space-xs

</style>
router
<!--Custom icons-->
<style>
    @font-face {
        font-family: 'custom icons';
        src: url('assets/custom-icons/custom-icons.eot');
        src: url('assets/custom-icons/custom-icons.eot?#iefix') format('embedded-opentype'),
            url('assets/custom-icons/custom-icons.woff') format('woff'),
            url('assets/custom-icons/custom-icons.ttf') format('truetype'),
            url('assets/custom-icons/custom-icons.svg#custom-icons') format('svg');
        font-weight: normal;
        font-style: normal;
    }
    [class*='icon-custom-']:before{
        display: inline-block;
        font-family: 'custom icons';
        font-style: normal;
        font-weight: normal;
        line-height: 1;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        letter-spacing: normal;
        white-space: nowrap;
    }
    .icon-custom-group:before{content:'\0041';}
    .icon-custom-home:before{content:'\0042';}
    .icon-custom-maintenance:before{content:'\0043';}
    .icon-custom-system:before{content:'\0044';}
    .icon-custom-user:before{content:'\0045';}

    .actionBtn {
        padding: 6px 12px;
        min-width: 105px;
        background-color: #1C4E7A;
        color: #fff;
        border: none;
        border-radius: 3px;
        transition: background-color 0.2s;
    }

    .actionBtn--small {
        padding: 3px 8px;
        min-width: 80px;
    }

    .actionBtn:hover {
        background-color: #FF8C42;
    }

    .actionBtn:disabled {
        background-color: #1C4E7A;
        opacity: 40%;
    }

    .cancelBtn {
        padding: 6px 12px;
        min-width: 105px;
        background-color: #fff;
        color: #1C4E7A;
        border: 1px solid #1C4E7A;
        border-radius: 3px;
        transition: background-color 0.2s;
    }

    .cancelBtn:hover {
        background-color: #E8E8E8;
    }

    .error {
        color: #fb725f;
        font-weight: 600;
    }
</style>

<script>

import inactivity from './mixins/login/inactivity'

export default {
  mixins: [inactivity],
  data () {
    return {
      isOnline: navigator.onLine
    }
  },
  methods: {
    updateConnectionStatus () {
      const previousStatus = this.isOnline
      this.isOnline = navigator.onLine

      if (previousStatus !== this.isOnline) {
        this.showConnectionToast(this.isOnline)
      }
    },
    showConnectionToast (isOnline) {
      const h = this.$createElement

      if (!isOnline) {
        const offlineMessage = h('div', { class: 'connection-toast-content' }, [
          h('b-icon', { props: { icon: 'wifi-off' } }),
          h('span', 'Sem conexao com a internet.')
        ])

        this.$bvToast.toast(
          offlineMessage,
          {
            id: 'connection-offline-toast',
            title: 'Conexao',
            toaster: 'b-toaster-top-center',
            variant: 'danger',
            noAutoHide: true,
            solid: true,
            appendToast: false
          }
        )
        return
      }

      this.$bvToast.hide('connection-offline-toast')
      const onlineMessage = h('div', { class: 'connection-toast-content' }, [
        h('b-icon', { props: { icon: 'wifi' } }),
        h('span', 'Conexao restabelecida.')
      ])

      this.$bvToast.toast(
        onlineMessage,
        {
          title: 'Conexao',
          toaster: 'b-toaster-top-center',
          variant: 'success',
          autoHideDelay: 3000,
          solid: true
        }
      )
    }
  },
  mounted () {
    window.addEventListener('online', this.updateConnectionStatus)
    window.addEventListener('offline', this.updateConnectionStatus)

    if (!this.isOnline) {
      this.showConnectionToast(false)
    }
  },
  beforeDestroy () {
    window.removeEventListener('online', this.updateConnectionStatus)
    window.removeEventListener('offline', this.updateConnectionStatus)
  }
}
</script>
